import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import {
  ListChecks, Mail, Phone, MapPin, Search, Loader2, Download, Sparkles, ShieldAlert,
  CheckCircle2, Send, MapPinned,
} from "lucide-react";

const TIMELINE_LABEL = {
  asap: "ASAP",
  "3_months": "Within 3 months",
  "6_months": "Within 6 months",
  "1_year": "Within 1 year",
  flexible: "Flexible",
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
};

const StateWaitlistTab = () => {
  const { token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listType, setListType] = useState("all");          // all | priority | general
  const [stateFilter, setStateFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (listType !== "all") params.set("list_type", listType);
      if (stateFilter !== "all") params.set("state", stateFilter);
      const res = await fetch(`${API}/admin/state-waitlist?${params.toString()}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setEntries(json.entries || []);
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [listType, stateFilter]);  // eslint-disable-line

  // ── State approval (flip COMING_SOON → READY + email magic links) ──────
  const [enroll, setEnroll] = useState({ ready: [], counts: {}, state_names: {} });
  const [approving, setApproving] = useState(null);

  const loadEnroll = async () => {
    try {
      const res = await fetch(`${API}/admin/state-enrollment`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (res.ok) setEnroll(await res.json());
    } catch { /* ignore */ }
  };
  useEffect(() => { loadEnroll(); }, []);  // eslint-disable-line

  const approveState = async (code) => {
    const name = enroll.state_names[code] || code;
    const cnt = enroll.counts[code]?.total || 0;
    if (!window.confirm(`Approve ${name} and email ${cnt} waitlisted ${cnt === 1 ? "person" : "people"} a magic application link?`)) return;
    setApproving(code);
    try {
      const res = await fetch(`${API}/admin/state-enrollment/${code}/approve`, {
        method: "POST",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      toast.success(`${d.state_name} is now open — emailed ${d.emailed} ${d.emailed === 1 ? "person" : "people"}.`);
      await loadEnroll();
      await load();
    } catch {
      toast.error("Could not approve state. Please try again.");
    } finally {
      setApproving(null);
    }
  };

  const pendingDemand = useMemo(() => (
    Object.entries(enroll.counts || {})
      .filter(([code]) => !(enroll.ready || []).includes(code))
      .map(([code, c]) => ({ code, name: enroll.state_names?.[code] || code, ...c }))
      .sort((a, b) => b.total - a.total)
  ), [enroll]);

  const filtered = useMemo(() => {
    if (!q.trim()) return entries;
    const needle = q.toLowerCase();
    return entries.filter(e =>
      (e.name || "").toLowerCase().includes(needle) ||
      (e.email || "").toLowerCase().includes(needle) ||
      (e.state_name || "").toLowerCase().includes(needle) ||
      (e.zip_code || "").includes(needle)
    );
  }, [entries, q]);

  const stateCodes = useMemo(() => {
    return Array.from(new Set(entries.map(e => e.state_code).filter(Boolean))).sort();
  }, [entries]);

  const priorityCount = entries.filter(e => e.list_type === "priority").length;
  const generalCount = entries.filter(e => e.list_type === "general").length;

  const exportCsv = () => {
    const cols = ["name", "email", "phone", "zip_code", "state_code", "state_name", "program_of_interest", "ideal_timeline", "list_type", "created_at"];
    const csv = [cols.join(",")]
      .concat(filtered.map(e => cols.map(c => {
        const v = (e[c] ?? "").toString().replace(/"/g, '""');
        return /[",\n]/.test(v) ? `"${v}"` : v;
      }).join(",")))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `state-waitlist-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="state-enrollment-section">
      {/* ── State Approval / Demand ──────────────────────────────────── */}
      <Card className="card-surface" data-testid="state-approval-card">
        <CardHeader className="space-y-0">
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <MapPinned className="w-5 h-5 text-brand-navy" />
            State Approval &amp; Demand
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            Approving a state opens enrollment for its residents and emails everyone on its
            waitlist a one-click magic link to start their application.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">Approved states (open for enrollment)</p>
            <div className="flex flex-wrap gap-2" data-testid="approved-states-list">
              {(enroll.ready || []).length === 0 ? (
                <span className="text-sm text-slate-400">None yet</span>
              ) : (enroll.ready || []).map((code) => (
                <Badge key={code} className="bg-emerald-100 text-emerald-700 hover:bg-emerald-200 gap-1" data-testid={`approved-state-${code}`}>
                  <CheckCircle2 className="w-3 h-3" /> {enroll.state_names?.[code] || code}
                </Badge>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">Coming-soon states with waitlist demand</p>
            {pendingDemand.length === 0 ? (
              <p className="text-sm text-slate-400" data-testid="no-pending-demand">No waitlist demand from coming-soon states yet.</p>
            ) : (
              <div className="space-y-2" data-testid="pending-demand-list">
                {pendingDemand.map((s) => (
                  <div key={s.code} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5" data-testid={`pending-state-${s.code}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <MapPin className="w-4 h-4 text-brand-navy shrink-0" />
                      <span className="font-medium text-slate-800 truncate">{s.name}</span>
                      <Badge variant="outline" className="border-slate-300 text-slate-600 shrink-0">
                        {s.total} waiting
                      </Badge>
                    </div>
                    <Button
                      size="sm"
                      className="btn-primary gap-1.5 shrink-0"
                      disabled={approving === s.code}
                      onClick={() => approveState(s.code)}
                      data-testid={`approve-state-btn-${s.code}`}
                    >
                      {approving === s.code
                        ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Approving…</>
                        : <><Send className="w-3.5 h-3.5" /> Approve &amp; Notify</>}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="card-surface" data-testid="state-waitlist-tab">
      <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <ListChecks className="w-5 h-5 text-brand-navy" />
            State-Level Waitlist
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            People who applied from a state where we’re not yet operational.
            Priority list = states with specialized forms in development. General = everywhere else.
          </p>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <Badge className="bg-brand-gold/15 text-brand-navy hover:bg-brand-gold/20" data-testid="waitlist-priority-count">
            <Sparkles className="w-3 h-3 mr-1" /> Priority {priorityCount}
          </Badge>
          <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-300" data-testid="waitlist-general-count">
            <ShieldAlert className="w-3 h-3 mr-1" /> General {generalCount}
          </Badge>
          <Button size="sm" variant="outline" onClick={exportCsv} disabled={!filtered.length} className="gap-2" data-testid="waitlist-export-btn">
            <Download className="w-4 h-4" /> Export CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400 pointer-events-none" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, state, ZIP…"
              className="input-light pl-9"
              data-testid="waitlist-search"
            />
          </div>
          <Select value={listType} onValueChange={setListType}>
            <SelectTrigger className="input-light w-44" data-testid="waitlist-list-type-trigger"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white border-slate-200">
              <SelectItem value="all">All Lists</SelectItem>
              <SelectItem value="priority">Priority Only</SelectItem>
              <SelectItem value="general">General Only</SelectItem>
            </SelectContent>
          </Select>
          <Select value={stateFilter} onValueChange={setStateFilter}>
            <SelectTrigger className="input-light w-44" data-testid="waitlist-state-trigger"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white border-slate-200 max-h-72">
              <SelectItem value="all">All States</SelectItem>
              {stateCodes.map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-12 text-slate-500 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading entries…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-slate-500" data-testid="waitlist-empty">
            No waitlist entries match your filters.
          </div>
        ) : (
          <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid="waitlist-table">
            <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider font-bold text-slate-500">
              <div className="col-span-3">Name &amp; Contact</div>
              <div className="col-span-2">State</div>
              <div className="col-span-2">Program</div>
              <div className="col-span-2">Timeline</div>
              <div className="col-span-2">Joined</div>
              <div className="col-span-1 text-right">List</div>
            </div>
            <div className="divide-y divide-slate-100">
              {filtered.map((e) => (
                <div key={e.entry_id} className="grid grid-cols-12 gap-3 px-4 py-3 text-sm hover:bg-slate-50 transition" data-testid={`waitlist-row-${e.entry_id}`}>
                  <div className="col-span-3 min-w-0">
                    <p className="font-medium text-slate-900 truncate">{e.name || "—"}</p>
                    <p className="text-xs text-slate-500 truncate flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {e.email}
                    </p>
                    {e.phone && (
                      <p className="text-xs text-slate-400 truncate flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {e.phone}
                      </p>
                    )}
                  </div>
                  <div className="col-span-2 min-w-0">
                    <p className="text-slate-800 truncate flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-brand-navy" />
                      {e.state_name || e.state_code || "—"}
                    </p>
                    {e.zip_code && <p className="text-xs text-slate-400">{e.zip_code}</p>}
                  </div>
                  <div className="col-span-2 min-w-0">
                    <p className="text-slate-700 truncate text-xs">{e.program_of_interest || "—"}</p>
                  </div>
                  <div className="col-span-2 min-w-0">
                    <p className="text-slate-700 text-xs">{TIMELINE_LABEL[e.ideal_timeline] || "—"}</p>
                  </div>
                  <div className="col-span-2 text-xs text-slate-500">{formatDate(e.created_at)}</div>
                  <div className="col-span-1 text-right">
                    {e.list_type === "priority" ? (
                      <Badge className="bg-brand-gold/15 text-brand-navy">Priority</Badge>
                    ) : (
                      <Badge variant="outline" className="border-slate-300 text-slate-600">General</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
    </div>
  );
};

export default StateWaitlistTab;
